import { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import {
  IressButton,
  IressInline,
  IressModal,
  IressText,
  IressStack,
} from '@iress-oss/ids-components';
import { ApiError } from '@/lib/errors/ApiError';
import { NavigableError } from '@/lib/errors/NavigableError';
import { RetryableError } from '@/lib/errors/RetryableError';
import { translateError } from '@/lib/errors/translateError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

/**
 * Button that navigates to a route via TanStack Router.
 * Extracted so the class-based ErrorBoundary can use the `useNavigate` hook.
 */
function NavigateButton({
  to,
  search,
  label,
  mode = 'primary',
  queryKeys = [],
  onNavigate,
}: Readonly<{
  to: string;
  search?: Record<string, unknown>;
  label: string;
  mode?: 'primary' | 'secondary';
  queryKeys?: QueryKey[];
  onNavigate: () => void;
}>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return (
    <IressButton
      mode={mode}
      onClick={async () => {
        // Invalidate queries before navigation so fresh data is fetched
        for (const queryKey of queryKeys) {
          queryClient.removeQueries({ queryKey, exact: true });
        }
        await navigate({ to, search });
        onNavigate();
      }}
    >
      {label}
    </IressButton>
  );
}

/**
 * Check if the error is a session expired error (401 Unauthorized).
 * Session errors should not show navigation buttons since the user
 * needs to sign in again via the host app.
 */
function isSessionExpiredError(error?: Error): boolean {
  return error instanceof ApiError && [401, 403].includes(error.status);
}

/**
 * Footer content for retryable errors.
 * Shows retry count and appropriate buttons based on whether more retries are available.
 */
function RetryableErrorFooter({
  error,
  retryCount,
  onRetry,
  onNavigate,
}: Readonly<{
  error: RetryableError;
  retryCount: number;
  onRetry: () => void;
  onNavigate: () => void;
}>) {
  const canRetry = retryCount < error.maxRetries;
  const queryClient = useQueryClient();

  const handleRetry = () => {
    // Clear cached query results so the hook will re-fetch on remount
    // Use exact: true to avoid removing queries that share a prefix
    for (const queryKey of error.queryKeys) {
      queryClient.removeQueries({ queryKey, exact: true });
    }
    onRetry();
  };

  return (
    <IressStack gap="spacing.3" horizontalAlign="center" mt="-spacing.4">
      <IressText textStyle="typography.body.sm" color="muted">
        {error.maxRetries - retryCount} retries remaining
      </IressText>
      <IressInline gap="spacing.3" horizontalAlign="center">
        {canRetry && (
          <IressButton mode="primary" onClick={handleRetry}>
            Retry
          </IressButton>
        )}
        <NavigateButton
          to="/"
          label="Go home"
          mode={canRetry ? 'secondary' : 'primary'}
          onNavigate={onNavigate}
        />
      </IressInline>
    </IressStack>
  );
}

/**
 * Error Boundary component using IDS components.
 * Translates API, Zod, and JS errors into user-friendly messages.
 *
 * Supports special error types:
 * - NavigableError: Custom navigation button
 * - RetryableError: Retry button with attempt tracking (maxRetries from error)
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to monitoring service (handled by Hydrofoil)
    console.error('Error caught by boundary:', error, errorInfo);
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    // A retry only commits with hasError: false if the children actually
    // rendered without throwing again — if they threw immediately,
    // getDerivedStateFromError re-sets hasError: true in the same update,
    // so this transition only fires on a genuinely successful retry.
    // Reset here (not in handleRetry) so an unrelated later failure gets
    // a fresh budget instead of inheriting a reduced one.
    if (
      prevState.hasError &&
      !this.state.hasError &&
      this.state.retryCount > 0
    ) {
      this.setState({ retryCount: 0 });
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, retryCount: 0 });
  };

  private handleRetry = () => {
    // Increment retry count and clear error to re-render children
    this.setState((prev) => ({
      hasError: false,
      error: undefined,
      retryCount: prev.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error } = this.state;
      const isRetryable = error instanceof RetryableError;
      const isNavigable = error instanceof NavigableError;
      const isSessionExpired = isSessionExpiredError(error);
      const message = translateError(error);

      // Extract NavigableError properties with type narrowing
      const navTo = isNavigable ? error.to : '/';
      const navSearch = isNavigable ? error.search : undefined;
      const navLabel = isNavigable ? error.buttonLabel : 'Go home';
      const navQueryKeys = isNavigable ? error.queryKeys : [];

      // Determine footer based on error type
      // Session expired errors don't show navigation (user must re-auth via host app)
      // Retryable errors show retry button with count
      // NavigableError and default errors show navigation button
      let footer: ReactNode = undefined;
      if (!isSessionExpired) {
        if (isRetryable) {
          footer = (
            <RetryableErrorFooter
              error={error}
              retryCount={this.state.retryCount}
              onRetry={this.handleRetry}
              onNavigate={this.handleReset}
            />
          );
        } else {
          footer = (
            <IressInline horizontalAlign="center" mt="-spacing.6">
              <NavigateButton
                to={navTo}
                search={navSearch}
                label={navLabel}
                queryKeys={navQueryKeys}
                onNavigate={this.handleReset}
              />
            </IressInline>
          );
        }
      }

      return (
        <IressModal
          status="danger"
          show={this.state.hasError}
          heading="Something went wrong"
          footer={footer}
          size="sm"
          noCloseButton
        >
          {message.includes('\n\n')
            ? message
                .split('\n\n')
                .map((paragraph, index) => (
                  <p key={`${index}-${paragraph}`}>{paragraph}</p>
                ))
            : message}
        </IressModal>
      );
    }

    return this.props.children;
  }
}
